import { getGroup } from '@/api/groups/group';
import { getInkind } from '@/api/inkinds/inkind';
import { getWishlistProducts } from '@/api/inkinds/wishlistProducts';
import { getPostsRelated } from '@/api/posts/postsRelated';
import type { PageLayoutProps } from '@/components/layout/PageLayout';
import { PageLayout } from '@/components/layout/PageLayout';
import { isBrowser } from '@/utils/environment';
import { logger } from '@gik/analytics/utils/logger';
import { useInkind } from '@gik/api/inkinds/inkind';
import type { FallbackProps, StaticProps } from '@gik/core/api/BaseAPIConfig';
import { baseFallback } from '@gik/core/api/BaseAPIConfig';
import type { Product } from '@gik/core/models/gik/Product';
import { catchSSRError } from '@gik/core/utils/catchSSRError';
import { measureSSRPerformance } from '@gik/core/utils/measureSSRPerformance';
import { InkindPage } from '@gik/inkind-page/components/InkindPage/InkindPage';
import { useInkindStore } from '@gik/inkind-page/store/InkindStore';
import { getRelatedArticlesForInkindPageFilters, relatedArticlesNumItems } from '@gik/ui/gik/RelatedArticles';
import type { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import React from 'react';
import { SWRConfig } from 'swr';

export default function InkindPageWrapper({ fallback }: FallbackProps<unknown>): React.ReactElement {
  const router = useRouter();

  const inkindRouteId = router.query.id as string;
  const inkindSlug = router.query.slug?.[0];

  const setInkindRouteId = useInkindStore(state => state.setInkindRouteId);
  setInkindRouteId(inkindRouteId);

  // Redirect to newest route ID and slug
  const { data: loadedInkindPage } = useInkind(inkindRouteId);
  const loadedInkindPageRouteId = loadedInkindPage?.routeId;
  const loadedInkindPageSlug = loadedInkindPage?.slug;
  React.useEffect(() => {
    if (
      isBrowser() &&
      loadedInkindPageRouteId &&
      (loadedInkindPageRouteId !== inkindRouteId || loadedInkindPageSlug !== inkindSlug)
    ) {
      window.location.href = `/inkinds/${loadedInkindPageRouteId}/${loadedInkindPageSlug}${window.location.search}`;
    }
  }, [inkindRouteId, inkindSlug, loadedInkindPageRouteId, loadedInkindPageSlug]);

  return (
    <SWRConfig value={{ fallback }}>
      <InkindPage />
    </SWRConfig>
  );
}

InkindPageWrapper.getLayout = function getLayout(page: React.ReactElement, layoutProps: PageLayoutProps) {
  return (
    <PageLayout {...layoutProps} key={layoutProps.key}>
      {page}
    </PageLayout>
  );
};

async function _getStaticProps(context: GetServerSidePropsContext): Promise<StaticProps<Product[]>> {
  let inkindRouteId = context.params.id as string;
  // ['null', 'undefined', 'admin'].indexOf(inkindRouteId) >= 0
  if (
    Array.isArray(context.params.slug) &&
    (context.params.slug.length > 1 || context.params.slug[0] === 'null' || context.params.slug[0] === 'undefined')
  ) {
    return { props: {} };
  }

  let inkindRouteSlug = context.params.slug?.[0];

  const inkindResponse = await getInkind(inkindRouteId);
  const inkindPage = await inkindResponse.json;

  if (inkindPage?.routeId !== inkindRouteId || inkindRouteSlug !== inkindPage?.slug) {
    // Cannot do a redirect here as the query parameters from the url will be dropped. (e.g. finish-registration)
    // Instead, update to use the updated the parameters for later queries. Redirect will occur on the frontend.
    // TODO: consider using a middleware in NextJS 12 https://github.com/vercel/next.js/discussions/17269
    inkindRouteId = inkindPage?.routeId;
    inkindRouteSlug = inkindPage?.slug;
    /*return {
      redirect: {
        destination: `/inkinds/${inkindPage.routeId}/${inkindPage.slug}`,
        permanent: true,
      },
    };*/
  }

  // TODO: stream user updates

  let similarProducts, wishlistProducts;
  try {
    // Temporarily do not request similar products from ISR calls in case of crawler spam
    [/*similarProducts, */ wishlistProducts] = await Promise.all([
      //getSimilarProducts(getInkindRelatedProductsSearchParams(inkindRouteId)),
      getWishlistProducts(inkindRouteId),
    ]);
  } catch (e) {
    logger.error(e);
  }

  let groupResponse;
  try {
    groupResponse = inkindPage?.groupId ? await getGroup(inkindPage?.groupId) : null;
  } catch (e) {
    logger.error(e);
  }

  let situationSlugs, relatedArticles;
  try {
    situationSlugs = inkindPage?.inkindSituations?.map(s => s.slug);

    relatedArticles = await getPostsRelated({
      relationFilters: getRelatedArticlesForInkindPageFilters(situationSlugs),
      perPage: relatedArticlesNumItems,
    });
  } catch (e) {
    logger.error(e);
  }

  return baseFallback({
    ...(inkindResponse ? { [inkindResponse.key]: inkindPage } : {}),
    //[similarProducts.key]: await similarProducts.json,
    ...(relatedArticles ? { [relatedArticles.key]: await relatedArticles.json } : {}),
    ...(wishlistProducts ? { [wishlistProducts.key]: await wishlistProducts.json } : {}),
    ...(groupResponse ? { [groupResponse.key]: await groupResponse.json } : {}),
  });
}

export const getStaticProps = catchSSRError(measureSSRPerformance(_getStaticProps, 'Inkind Page'));

export async function getStaticPaths() {
  // TODO: consider adding some popular pages to be cached at build time
  return { paths: [], fallback: 'blocking' };
}
